import axios from 'axios';
import { getApiUrl } from '../config/environment';

/**
 * Frontend Stripe service.
 *
 * The previous version had a catch-block that returned `{success: false, error}`
 * on ANY error from the backend, which the UI then interpreted as "backend not
 * ready" and silently simulated a successful payment. That meant any transient
 * auth/network/Stripe error gave the customer free service. This file removes
 * that behavior: errors propagate as real errors. Callers must handle them.
 *
 * Also: we only read the real JWT (`token`). The legacy `tempAuth` synthetic
 * token always 401'd the backend, which is what triggered the bypass.
 */

export interface CreatePaymentIntentData {
  amount: number;
  currency: string;
  orderId: string;
  customerEmail: string;
  description?: string;
}

export interface PaymentIntentResponse {
  clientSecret: string;
  paymentIntentId?: string;
  setupIntentId?: string;
  requiresSetup?: boolean;
  isSubscriptionSetup?: boolean;
  // NEW global-flow flag: when true, clientSecret is for a real PaymentIntent
  // (first-period charge), not a SetupIntent. Frontend must call
  // stripe.confirmCardPayment, not stripe.confirmCardSetup.
  isSubscriptionPayment?: boolean;
  amount?: number;
  currency?: string;
  subscriptionId?: string;
}

function getAuthToken(): string | null {
  return localStorage.getItem('token');
}

function authHeaders() {
  const token = getAuthToken();
  if (!token) {
    throw new Error('You must be signed in to start a payment. Please log in and try again.');
  }
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

export const stripeService = {
  async createPaymentIntent(data: CreatePaymentIntentData): Promise<PaymentIntentResponse> {
    const response = await axios.post(
      getApiUrl('/payments/payment-intent'),
      data,
      { headers: authHeaders() }
    );

    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Could not start payment. Please try again.');
    }

    const payload = response.data.data;
    if (!payload?.clientSecret) {
      throw new Error('Payment server returned an incomplete response. Please try again or contact support.');
    }

    return {
      clientSecret: payload.clientSecret,
      paymentIntentId: payload.paymentIntentId,
      setupIntentId: payload.setupIntentId,
      requiresSetup: payload.requiresSetup,
      isSubscriptionSetup: payload.isSubscriptionSetup,
      isSubscriptionPayment: payload.isSubscriptionPayment,
      amount: payload.amount,
      currency: payload.currency,
      subscriptionId: payload.subscriptionId,
    };
  },

  /**
   * Create a VPS subscription with card verification. Used by the VPS
   * checkout's pay-now path. Unchanged from previous behavior — keeps the
   * `{success, data, error}` envelope because the VPS flow's caller expects it.
   */
  async createVPSSubscription(data: {
    planId: string;
    paymentMethodId: string;
    billingCycle: 'monthly' | 'yearly';
  }): Promise<{
    success: boolean;
    data?: {
      subscriptionId: string;
      orderId: string;
      verificationRefunded: boolean;
      refundId: string;
      message: string;
    };
    error?: string;
  }> {
    try {
      const response = await axios.post(
        getApiUrl('/payments/create-vps-subscription'),
        data,
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('VPS subscription creation failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create VPS subscription',
      };
    }
  },
};
